import React, { useEffect, useState } from 'react';
import './CoachActionCard.css';

/**
 * CoachActionCard — the single, canonical way to render a "the coach did /
 * proposes X" event inline in the conversation thread.
 *
 * The backend emits a normalized action shape from askCoach():
 *   { type, icon, label, items? }
 * e.g. strategy_adjusted, metrics_recorded, metric_checkin_completed,
 *      media_checkin_completed.
 *
 * Rendering these as inline thread events (rather than bespoke per-surface
 * markup) keeps every coach interaction — strategy changes, recorded
 * metrics, document check-ins — visible as a confirmable beat in the same
 * conversation.
 */
const CoachActionCard = ({ action, backendService }) => {
  const [status, setStatus] = useState(action?.status === 'applied' ? 'applied' : 'pending');
  const [error, setError] = useState('');
  useEffect(() => {
    const handleResolved = event => {
      if (String(event?.detail?.change_id || '') === String(action?.change_id || '') && event?.detail?.status === 'applied') {
        setStatus('applied');
      }
    };
    window.addEventListener('coach-action-resolved', handleResolved);
    return () => window.removeEventListener('coach-action-resolved', handleResolved);
  }, [action?.change_id]);
  if (!action) return null;
  const items = Array.isArray(action.items) ? action.items : [];
  const isDojoNutritionConfirmation = action.type === 'dojo_nutrition_schedule_confirmation';
  const applySharedSchedule = async () => {
    if (!backendService?.applyPendingPlanChanges || status === 'applying') return;
    setStatus('applying');
    setError('');
    try {
      await backendService.applyPendingPlanChanges({
        scope: 'nutrition',
        mode: action.mode || 'add',
        change_id: action.change_id,
        strategy_id: action.strategy_id,
        strategy_revision: action.strategy_revision,
        target_meal_count: action.target_meal_count,
      });
      setStatus('applied');
      window.dispatchEvent(new CustomEvent('coach-action-resolved', {
        detail: { change_id: action.change_id, status: 'applied' }
      }));
    } catch (applyError) {
      setStatus('pending');
      setError(applyError?.message || 'Could not update the shared meal schedule.');
    }
  };
  return (
    <div className="coach-action-card">
      <div className="coach-action-card-header">
        <span className="coach-action-card-icon">{action.icon || '✅'}</span>
        <span className="coach-action-card-label">{action.label}</span>
        {action.status_label && <span className="coach-action-card-status">{action.status_label}</span>}
      </div>
      {items.length > 0 && (
        <ul className="coach-action-card-items">
          {items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      )}
      {action.impact_notice && <div className="coach-action-card-impact">{action.impact_notice}</div>}
      {isDojoNutritionConfirmation && status !== 'applied' && (
        <button type="button" className="coach-action-card-apply" onClick={applySharedSchedule} disabled={status === 'applying'}>
          {status === 'applying' ? 'Applying…' : 'Apply to everyone'}
        </button>
      )}
      {isDojoNutritionConfirmation && status === 'applied' && <div className="coach-action-card-applied">✓ Applied to the dojo</div>}
      {error && <div className="coach-action-card-error" role="alert">{error}</div>}
    </div>
  );
};

/**
 * Convenience wrapper to render a list of actions with consistent spacing.
 */
export const CoachActionCards = ({ actions, backendService }) => {
  const list = Array.isArray(actions) ? actions : [];
  if (list.length === 0) return null;
  return (
    <div className="coach-action-cards">
      {list.map((action, idx) => (
        <CoachActionCard key={`${idx}-${action?.type || 'action'}`} action={action} backendService={backendService} />
      ))}
    </div>
  );
};

export default CoachActionCard;
